import { useState, useMemo, useCallback, useEffect } from 'react';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';
import { SearchableMultiSelect, type MultiSelectOption } from '@/components/base/SearchableMultiSelect';

interface WorkOrderEditModalProps {
  open: boolean;
  workOrder: any | null;
  onClose: () => void;
  onSave: (updated: any) => void;
  machines: any[];
  operators: any[];
  shifts: any[];
  workCenters: any[];
  /** assign: crew, machine and planned time of a planned stage; start: the operator starts it */
  mode?: 'edit' | 'assign' | 'start' | 'rework';
}

// "YYYY-MM-DDTHH:mm" in local time, for datetime-local inputs
const toLocalInput = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Masters without a warehouse are shared; otherwise they must belong to the Work Order's warehouse
function inWarehouse(row: any, warehouseId: any): boolean {
  const wh = row.warehouseId ?? row.warehouse_id;
  return !warehouseId || !wh || String(wh) === String(warehouseId);
}

function isActiveRow(row: any): boolean {
  return row.isActive ?? row.is_active ?? row.isactive ?? true;
}

const sameName = (a: any, b: any) =>
  !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

const idList = (v: any): string[] => (Array.isArray(v) ? v.map(String) : []);

// The operators chosen for the machine in Masters → Machines (the machine's assigned operators,
// kept in step with each operator's allowed machines). One of them runs the machine; the rest of
// the stage's crew (extra labour from the work center) can be any operator.
function isMachineOperator(op: any, machine: any | null): boolean {
  if (!machine) return false;
  return idList(machine.assignedOperatorIds ?? machine.assigned_operator_ids).includes(String(op.id))
    || idList(op.allowedMachineIds ?? op.allowed_machine_ids).includes(String(machine.id));
}

const SKILL_LEVEL_RANK: Record<string, number> = { EXPERT: 0, SKILLED: 1, TRAINEE: 2 };
const SKILL_LEVEL_LABEL: Record<string, string> = { EXPERT: 'Expert', SKILLED: 'Skilled', TRAINEE: 'Trainee' };
const skillLevelOf = (op: any): string => String(op?.skillLevel ?? op?.skill_level ?? 'SKILLED').toUpperCase();

export default function WorkOrderEditModal({
  open,
  workOrder,
  onClose,
  onSave,
  machines,
  operators,
  shifts,
  workCenters,
  mode = 'edit',
}: WorkOrderEditModalProps) {
  const isStart = mode === 'start';
  const isAssign = mode === 'assign';
  // Rework: card creates a new Work Order for a QC-failed stage; date fields hold the planned dates
  const isRework = mode === 'rework';
  const [selectedWcId, setSelectedWcId] = useState('');
  const [machineId, setMachineId] = useState('');
  // The stage's crew; the first one is the lead operator
  const [operatorIds, setOperatorIds] = useState<string[]>([]);
  const [shiftId, setShiftId] = useState('');
  const [actualStart, setActualStart] = useState('');
  const [actualEnd, setActualEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [plannedQty, setPlannedQty] = useState('');
  // Assign: planned start / end date and time (end follows start by the stage's planned minutes)
  const [plannedStartAt, setPlannedStartAt] = useState('');
  const [plannedEndAt, setPlannedEndAt] = useState('');
  const [error, setError] = useState('');

  // Active work centers in this warehouse
  const warehouseWorkCenters = useMemo(() => {
    if (!workOrder) return [];
    return workCenters.filter((wc) => inWarehouse(wc, workOrder.warehouseId) && isActiveRow(wc));
  }, [workOrder, workCenters]);

  const workCenterOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '— Select Work Center —' },
    ...warehouseWorkCenters.map((wc) => ({
      value: String(wc.id),
      label: wc.name,
      badge: wc.type ? (String(wc.type).toUpperCase() === 'BOTH' ? 'Machine + Labor' : wc.type) : undefined,
    })),
  ], [warehouseWorkCenters]);

  // Work center resolved from selected id, or falling back to current work order
  const currentWorkCenter = useMemo(() => {
    if (selectedWcId) {
      return workCenters.find((wc) => String(wc.id) === String(selectedWcId)) || null;
    }
    if (!workOrder) return null;
    return (
      workCenters.find((wc) => String(wc.id) === String(workOrder.workCenterId)) ||
      workCenters.find((wc) => inWarehouse(wc, workOrder.warehouseId) && sameName(wc.name, workOrder.workCenterName)) ||
      null
    );
  }, [selectedWcId, workOrder, workCenters]);

  // Active machines in this warehouse, filtered by selected work center if chosen
  const filteredMachines = useMemo(() => {
    if (!workOrder) return [];
    const activeMachines = machines.filter((m) => inWarehouse(m, workOrder.warehouseId) && isActiveRow(m));
    if (!selectedWcId) return activeMachines;
    return activeMachines.filter((m) => {
      const wcId = m.workCenterId ?? m.work_center_id ?? m.workcenterid;
      return String(wcId) === String(selectedWcId);
    });
  }, [workOrder, selectedWcId, machines]);

  // Active operators of the warehouse; with a machine, only the ones chosen for it
  const warehouseOperators = useMemo(() => {
    if (!workOrder) return [];
    return operators.filter((o) => isActiveRow(o) && inWarehouse(o, workOrder.warehouseId));
  }, [workOrder, operators]);

  const selectedMachine = useMemo(
    () => machines.find((m) => String(m.id) === String(machineId)) || null,
    [machines, machineId],
  );

  // Crew size the stage needs (routing stage / work center persons)
  const crew = Math.max(1, Number(workOrder?.operatorsRequired) || 1);

  // Machine operators first, then everyone else of the warehouse (extra labour); by skill
  const machineOperatorIds = useMemo(
    () => new Set(warehouseOperators.filter((o) => isMachineOperator(o, selectedMachine)).map((o) => String(o.id))),
    [warehouseOperators, selectedMachine],
  );
  const filteredOperators = useMemo(() => {
    const bySkill = (a: any, b: any) => (SKILL_LEVEL_RANK[skillLevelOf(a)] ?? 1) - (SKILL_LEVEL_RANK[skillLevelOf(b)] ?? 1);
    return [...warehouseOperators].sort((a, b) =>
      Number(machineOperatorIds.has(String(b.id))) - Number(machineOperatorIds.has(String(a.id))) || bySkill(a, b));
  }, [warehouseOperators, machineOperatorIds]);
  const needsMachineOperator = machineOperatorIds.size > 0;
  const hasMachineOperator = operatorIds.some((id) => machineOperatorIds.has(id));

  const handleWorkCenterChange = useCallback(
    (newWcId: string) => {
      setSelectedWcId(newWcId);
      // Clear machine if it doesn't belong to newly selected work center
      if (newWcId && machineId) {
        const mc = machines.find((m) => String(m.id) === String(machineId));
        const mcWc = mc?.workCenterId ?? mc?.work_center_id ?? mc?.workcenterid;
        if (mcWc && String(mcWc) !== String(newWcId)) {
          setMachineId('');
        }
      }
    },
    [machineId, machines],
  );

  // Picking another machine automatically aligns the work center if not already aligned
  const handleMachineChange = useCallback(
    (id: string) => {
      setMachineId(id);
      if (id) {
        const machine = machines.find((m) => String(m.id) === String(id));
        const mcWc = machine?.workCenterId ?? machine?.work_center_id ?? machine?.workcenterid;
        if (mcWc && String(mcWc) !== String(selectedWcId)) {
          setSelectedWcId(String(mcWc));
        }
      }
    },
    [machines, selectedWcId],
  );

  const filteredShifts = useMemo(() => {
    if (!workOrder) return [];
    return shifts.filter((sh) => isActiveRow(sh) && inWarehouse(sh, workOrder.warehouseId));
  }, [workOrder, shifts]);

  // Picking an operator pre-selects their assigned shift when none is chosen yet
  const handleOperatorChange = useCallback(
    (ids: string[]) => {
      // Never more than the crew the stage needs
      const next = ids.slice(0, crew);
      setOperatorIds(next);
      const op = filteredOperators.find((o) => String(o.id) === String(next[0]));
      const opShift = op?.shiftId ?? op?.shift_id;
      if (opShift && !shiftId && filteredShifts.some((sh) => String(sh.id) === String(opShift))) {
        setShiftId(String(opShift));
      }
    },
    [filteredOperators, filteredShifts, shiftId, crew],
  );

  // Reset form when workOrder changes or modal is opened
  useEffect(() => {
    if (workOrder && open) {
      const initialWc =
        workCenters.find((wc) => String(wc.id) === String(workOrder.workCenterId)) ||
        workCenters.find((wc) => inWarehouse(wc, workOrder.warehouseId) && sameName(wc.name, workOrder.workCenterName));
      setSelectedWcId(initialWc ? String(initialWc.id) : (workOrder.workCenterId ? String(workOrder.workCenterId) : ''));

      setMachineId(String(workOrder.machineId || machines.find((m) => sameName(m.name, workOrder.machineName))?.id || ''));
      const savedCrew: string[] = Array.isArray(workOrder.operatorIds) && workOrder.operatorIds.length
        ? workOrder.operatorIds.map(String)
        : [String(workOrder.operatorId || operators.find((o) => sameName(o.name, workOrder.operatorName))?.id || '')].filter(Boolean);
      setOperatorIds(savedCrew);
      setShiftId(String(workOrder.shiftId || filteredShifts.find((sh) => sameName(sh.name, workOrder.shiftName))?.id || ''));
      const startAt = workOrder.plannedStartAt
        ? new Date(workOrder.plannedStartAt)
        : new Date(`${workOrder.plannedStartDate || new Date().toLocaleDateString('en-CA')}T09:00`);
      setPlannedStartAt(toLocalInput(startAt));
      setPlannedEndAt(toLocalInput(workOrder.plannedEndAt
        ? new Date(workOrder.plannedEndAt)
        : new Date(startAt.getTime() + Math.max(Number(workOrder.plannedTimeMinutes) || 0, 1) * 60000)));
      const today = new Date().toLocaleDateString('en-CA');
      if (isRework) {
        setActualStart(today);
        setActualEnd(today);
        setNotes('');
        setPlannedQty(String(Number(workOrder.rejectedQty) > 0 ? workOrder.rejectedQty : workOrder.plannedQty || ''));
      } else {
        setActualStart(workOrder.actualStartDate || (isStart ? today : ''));
        setActualEnd(workOrder.actualEndDate || '');
        setNotes(workOrder.notes || '');
      }
      setError('');
    }
  }, [workOrder, open, isStart, isRework, isAssign, workCenters, machines, operators, filteredShifts]);

  const machineOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '— Not assigned —' },
    ...filteredMachines.map((m) => {
      const mcWc = workCenters.find((w) => String(w.id) === String(m.workCenterId ?? m.work_center_id));
      return {
        value: String(m.id),
        label: m.name,
        sublabel: [
          m.machineCode || m.code ? `Code: ${m.machineCode || m.code}` : null,
          m.model ? `Model: ${m.model}` : null,
          !selectedWcId && mcWc?.name ? `WC: ${mcWc.name}` : null,
        ].filter(Boolean).join(' · '),
      };
    }),
  ], [filteredMachines, workCenters, selectedWcId]);

  const operatorOptions: MultiSelectOption[] = useMemo(() => filteredOperators.map((o) => ({
    value: String(o.id),
    label: o.name,
    badge: needsMachineOperator ? (machineOperatorIds.has(String(o.id)) ? 'Machine operator' : 'Extra labour') : undefined,
    sublabel: [
      o.employeeCode || o.employee_code ? `Code: ${o.employeeCode || o.employee_code}` : (o.skill || ''),
      SKILL_LEVEL_LABEL[skillLevelOf(o)],
    ].filter(Boolean).join(' · '),
  })), [filteredOperators, needsMachineOperator, machineOperatorIds]);

  const selectedOperators = filteredOperators.filter((o) => operatorIds.includes(String(o.id)));

  const shiftOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '— Not assigned —' },
    ...filteredShifts.map((s) => ({
      value: String(s.id),
      label: `${s.name} (${s.startTime || s.start_time || ''}–${s.endTime || s.end_time || ''})`,
    })),
  ], [filteredShifts]);

  const handleSave = useCallback(() => {
    if (!workOrder) return;
    if (isStart || isRework || isAssign) {
      const missing = [
        filteredMachines.length > 0 && !machineId && 'Machine',
        operatorIds.length === 0 && 'Operator',
        !shiftId && 'Shift',
        isRework && !(Number(plannedQty) > 0) && 'Planned Qty',
        isAssign ? !plannedStartAt && 'Planned Start' : !actualStart && (isRework ? 'Planned Start' : 'Actual Start'),
        isRework && !actualEnd && 'Planned End',
      ].filter(Boolean);
      if (missing.length > 0) {
        setError(`Please fill required details: ${missing.join(', ')}`);
        return;
      }
      if (needsMachineOperator && !hasMachineOperator) {
        setError(`${selectedMachine?.name || 'This machine'} must be run by one of its operators — add one of them to the crew`);
        return;
      }
      if (isAssign && plannedEndAt && plannedEndAt <= plannedStartAt) {
        setError('Planned End must be after Planned Start');
        return;
      }
      if (isRework && actualEnd < actualStart) {
        setError('Planned End cannot be before Planned Start');
        return;
      }
    }
    setError('');
    const machine = machines.find((m) => String(m.id) === String(machineId));
    // The lead operator is the first machine operator chosen, else the first of the crew
    const ordered = [...operatorIds].sort((a, b) => Number(machineOperatorIds.has(b)) - Number(machineOperatorIds.has(a)));
    const crewNames = ordered.map((id) => operators.find((o) => String(o.id) === id)?.name).filter(Boolean);
    const shift = shifts.find((s) => String(s.id) === String(shiftId));
    const chosenWc = workCenters.find((w) => String(w.id) === String(selectedWcId));

    const updated = {
      ...workOrder,
      workCenterId: selectedWcId || workOrder.workCenterId,
      workCenterName: chosenWc?.name || currentWorkCenter?.name || workOrder.workCenterName,
      machineId: machineId || null,
      machineName: machine?.name || null,
      operatorId: ordered[0] || null,
      operatorIds: ordered,
      operatorName: crewNames.join(', ') || null,
      shiftId: shiftId || null,
      shiftName: shift?.name || null,
      ...(isRework
        ? { plannedQty: Number(plannedQty), plannedStartDate: actualStart, plannedEndDate: actualEnd }
        : isAssign
          ? { plannedStartAt, plannedEndAt }
          : { actualStartDate: actualStart, actualEndDate: actualEnd }),
      notes: notes || null,
    };
    onSave(updated);
  }, [
    workOrder,
    selectedWcId,
    currentWorkCenter,
    isStart,
    isRework,
    isAssign,
    plannedStartAt,
    plannedEndAt,
    plannedQty,
    filteredMachines,
    machineId,
    operatorIds,
    machineOperatorIds,
    needsMachineOperator,
    hasMachineOperator,
    selectedMachine,
    shiftId,
    actualStart,
    actualEnd,
    notes,
    machines,
    operators,
    shifts,
    workCenters,
    onSave,
  ]);

  if (!open || !workOrder) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-end sm:justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full sm:w-[500px] sm:rounded-xl shadow-2xl sm:max-h-[90vh] overflow-y-auto rounded-t-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-base font-semibold text-[#1e293b]">
              {isRework ? 'Create Rework Work Order' : isAssign ? 'Assign Work Order' : isStart ? 'Start Work Order' : 'Edit Work Order'}
            </h3>
            <p className="text-xs text-[#64748b] mt-0.5">
              {workOrder.woNumber} — {workOrder.stageName}
            </p>
            <p className="text-xs text-[#64748b] mt-0.5">
              {[
                workOrder.productionOrderNumber,
                workOrder.itemName,
                workOrder.plannedQty ? `Qty ${workOrder.plannedQty}` : null,
                currentWorkCenter?.name || workOrder.workCenterName,
                workOrder.warehouseName,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Work Center */}
          <div>
            <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
              Work Center
            </label>
            <SearchableSelect
              value={selectedWcId}
              onChange={handleWorkCenterChange}
              options={workCenterOptions}
              placeholder="— Select Work Center —"
              searchPlaceholder="Search work centers..."
            />
          </div>

          {/* Machine */}
          <div>
            <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
              Machine
            </label>
            <SearchableSelect
              value={machineId}
              onChange={handleMachineChange}
              options={machineOptions}
              placeholder="— Not assigned —"
              searchPlaceholder="Search machine..."
            />
            {filteredMachines.length === 0 && selectedWcId && (
              <p className="text-[11px] text-amber-600 mt-1">
                No active machines found for {currentWorkCenter?.name || 'selected work center'}.
              </p>
            )}
          </div>

          {/* Operators: the stage's crew */}
          <div>
            <label className="flex items-center justify-between text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
              <span>Operators</span>
              <span className={`normal-case tracking-normal font-semibold ${operatorIds.length === crew ? 'text-emerald-600' : 'text-amber-600'}`}>
                {operatorIds.length} of {crew} required
              </span>
            </label>
            <SearchableMultiSelect
              value={operatorIds}
              onChange={handleOperatorChange}
              options={operatorOptions}
              placeholder="— Not assigned —"
              searchPlaceholder="Search operator..."
              noneSelectedText="— Not assigned —"
            />
            {warehouseOperators.length === 0 ? (
              <p className="text-[11px] text-amber-600 mt-1">No active operators found for this warehouse</p>
            ) : needsMachineOperator ? (
              <p className={`text-[11px] mt-1 ${operatorIds.length > 0 && !hasMachineOperator ? 'text-amber-600' : 'text-[#64748b]'}`}>
                {selectedMachine?.name || 'The machine'} is run by one of its operators ({filteredOperators.filter((o) => machineOperatorIds.has(String(o.id))).map((o) => o.name).join(', ')})
                {crew > 1 ? `; the other ${crew - 1} can be any operator (extra labour)` : ''}.
              </p>
            ) : (
              <p className="text-[11px] text-[#64748b] mt-1">
                {selectedMachine ? `No operators are set on ${selectedMachine.name}, so any operator can be chosen.` : 'Any operator can be chosen.'}
                {crew > 1 ? ` This stage needs ${crew}.` : ''}
              </p>
            )}
            {operatorIds.length >= crew && crew > 0 && (
              <p className="text-[11px] text-[#94a3b8] mt-0.5">Crew is full — remove one to pick another.</p>
            )}
            {selectedOperators.some((o) => skillLevelOf(o) === 'TRAINEE') && (
              <p className="text-[11px] text-amber-600 mt-1">
                <i className="ri-eye-line" /> Trainee in the crew — make sure a supervisor oversees this job
              </p>
            )}
          </div>

          {/* Shift */}
          <div>
            <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
              Shift
            </label>
            <SearchableSelect
              value={shiftId}
              onChange={setShiftId}
              options={shiftOptions}
              placeholder="— Not assigned —"
              searchPlaceholder="Search shift..."
            />
          </div>

          {isRework && (
            <div>
              <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                Planned Qty
              </label>
              <input
                type="number"
                min={0}
                step="any"
                value={plannedQty}
                onChange={(e) => setPlannedQty(e.target.value)}
                className="w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100"
              />
            </div>
          )}

          {/* Planned date and time (assign) */}
          {isAssign && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">Planned Start</label>
                <input
                  type="datetime-local"
                  value={plannedStartAt}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPlannedStartAt(v);
                    if (v) setPlannedEndAt(toLocalInput(new Date(new Date(v).getTime() + Math.max(Number(workOrder.plannedTimeMinutes) || 0, 1) * 60000)));
                  }}
                  className="w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">Planned End</label>
                <input
                  type="datetime-local"
                  value={plannedEndAt}
                  onChange={(e) => setPlannedEndAt(e.target.value)}
                  className="w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100"
                />
              </div>
              <p className="col-span-2 -mt-1 text-[11px] text-[#94a3b8]">
                Est. duration {Number(workOrder.plannedTimeMinutes) || 0} min (routing time for the quantity). The operator starts it from My Work Orders.
              </p>
            </div>
          )}

          {/* Actual Dates */}
          {!isAssign && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                {isRework ? 'Planned Start' : 'Actual Start'}
              </label>
              <input
                type="date"
                value={actualStart ? actualStart.split('T')[0] : ''}
                onChange={(e) => setActualStart(e.target.value)}
                className="w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                {isRework ? 'Planned End' : 'Actual End'}
              </label>
              <input
                type="date"
                value={actualEnd ? actualEnd.split('T')[0] : ''}
                onChange={(e) => setActualEnd(e.target.value)}
                disabled={isStart}
                className="w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
              />
            </div>
          </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100 resize-none"
              placeholder={isRework ? 'Rework instructions...' : 'Optional notes...'}
            />
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="h-9 px-4 rounded-lg border border-slate-200 text-sm font-medium text-[#64748b] hover:bg-white cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-[#4338ca] cursor-pointer whitespace-nowrap"
          >
            {isRework ? 'Create Rework WO' : isAssign ? 'Assign Work Order' : isStart ? 'Start Work Order' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
